import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';

const API_HEALTH_ROOT = API_ROOT + '/api'; // https://sc.smartkulsary.kz/api/scadmin/api
const INDICATORS_ENDPOINT = '/health/indicators';
const RECORDS_ENDPOINT = '/health/records';

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

/* --------------------------- helpers перевода полей --------------------------- */

// первое не-null значение по списку возможных имён ключа (camel/snake)
function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return undefined;
}

// period_type → нижний регистр, как ждёт HealthPage ('year' | 'quarter')
function normPeriodType(v) {
  return String(v || '').toLowerCase() === 'quarter' ? 'quarter' : 'year';
}

// direction → 'higher_better' | 'lower_better' (принимаем HIGHER_BETTER, higherBetter …)
function normDirection(v) {
  return String(v || '')
    .toLowerCase()
    .includes('high')
    ? 'higher_better'
    : 'lower_better';
}

// Индикатор из ответа бэка → внутренняя модель фронта
function toIndicator(i, idx) {
  const so = pick(i, 'sort_order', 'sortOrder');
  return {
    code: pick(i, 'code'),
    name_ru: pick(i, 'name_ru', 'nameRu'),
    name_kk: pick(i, 'name_kz', 'nameKz', 'name_kk', 'nameKk'),
    unit_ru: pick(i, 'unit_ru', 'unitRu'),
    unit_kk: pick(i, 'unit_kz', 'unitKz', 'unit_kk', 'unitKk'),
    direction: normDirection(pick(i, 'direction')),
    sort_order: so != null ? so : idx + 1,
  };
}

// Строка перечня из ответа бэка → внутренняя модель фронта
function toRow(r) {
  const q = pick(r, 'quarter');
  return {
    id: pick(r, 'id'),
    indicator_code: pick(r, 'indicator_code', 'indicatorCode'),
    period_type: normPeriodType(pick(r, 'period_type', 'periodType')),
    year: pick(r, 'year'),
    quarter: q != null ? q : null,
    plan: pick(r, 'plan'),
    fact: pick(r, 'fact'),
    note: pick(r, 'note') != null ? pick(r, 'note') : null,
  };
}

/* ------------------------------ Справочник ------------------------------ */
export async function fetchHealthMeta() {
  const res = await getNetwork().get(API_HEALTH_ROOT + INDICATORS_ENDPOINT);
  const data = res.data || {};
  const list = Array.isArray(data.indicators) ? data.indicators : Array.isArray(data) ? data : [];
  return { indicators: list.map(toIndicator) };
}

/* ------------------------------- Перечень ------------------------------- */
// GET /records поддерживает серверные фильтры (indicatorCode, periodType, year,
// quarter, sort). Пока HealthPage фильтрует на клиенте, поэтому тянем всё разом.
export async function fetchHealthRows() {
  const res = await getNetwork().get(API_HEALTH_ROOT + RECORDS_ENDPOINT);
  const data = res.data || {};
  const raw = Array.isArray(data.rows) ? data.rows : Array.isArray(data) ? data : [];
  return { rows: raw.map(toRow) };
}

/* ------------------------------- Создание ------------------------------- */
// Тело POST — snake_case: бэк валидирует indicator_code / period_type (см. ответ
// сервера «validation_failed: indicator_code (required), period_type (required)»).
// Значение period_type — UPPER enum 'YEAR'/'QUARTER'.
const PERIOD_TYPE_OUT = { year: 'YEAR', quarter: 'QUARTER' };

function toBackendPayload(p) {
  return {
    indicator_code: p.indicator_code,
    period_type: PERIOD_TYPE_OUT[p.period_type] || p.period_type,
    year: p.year,
    quarter: p.period_type === 'quarter' ? p.quarter : null,
    plan: p.plan,
    fact: p.fact,
    note: p.note != null && String(p.note).trim() ? p.note : null,
  };
}

// payload (внутренний, snake): { indicator_code, period_type, year, quarter|null, plan, fact, note|null }
export async function createHealthRow(payload) {
  try {
    const res = await getNetwork().post(
      API_HEALTH_ROOT + RECORDS_ENDPOINT,
      toBackendPayload(payload)
    );
    if (res && (res.status === 201 || res.status === 200)) return toRow(res.data || {});
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}
